import { INVOICE_STATUS, PAYMENT_METHOD } from '@monorepo/shared/schemas/common'
import type {
  Invoice,
  InvoiceStatus,
  PaginatedInvoicesResponse,
} from '@monorepo/shared/schemas/invoices'
import { useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  Clock,
  CreditCard,
  Edit,
  Eye,
  FileCheck,
  FileText,
  Mail,
  MoreHorizontal,
  Receipt,
  RefreshCw,
  Send,
  XCircle,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import type { Customer } from '@/features/customers/services/customerService'
import { useEnvironment } from '@/features/environments/contexts/EnvironmentContext'
import {
  CreateInvoiceDialog,
  formatCentsAsCurrency,
  invoicesService,
  paymentsService,
  RecordPaymentDialog,
  SendInvoiceEmailDialog,
  UpdateInvoiceStatusDialog,
} from '@/features/invoices'
import { toast } from '@/lib/toast'
import { EnhancedTable } from '@/shared/components/table/EnhancedTable'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent, CardHeader } from '@/shared/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'

interface InvoiceTabProps {
  customer: Customer
}

export function InvoiceTab({ customer }: InvoiceTabProps) {
  const navigate = useNavigate()
  const { currentEnvironment } = useEnvironment()
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [summary, setSummary] =
    useState<PaginatedInvoicesResponse['summary']>(undefined)
  const [loading, setLoading] = useState(true)

  // Dialog states
  const [showRecordPaymentDialog, setShowRecordPaymentDialog] = useState(false)
  const [showUpdateStatusDialog, setShowUpdateStatusDialog] = useState(false)
  const [showEmailDialog, setShowEmailDialog] = useState(false)
  const [invoiceForAction, setInvoiceForAction] = useState<Invoice | null>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-fetch when environment changes
  const loadInvoices = useCallback(async () => {
    setLoading(true)
    try {
      const response = await invoicesService.getInvoices({
        customer_id: customer.id,
      })
      setInvoices(response.data)
      setSummary(response.summary)
    } catch {
      // Silently handled - loading state will clear
    } finally {
      setLoading(false)
    }
  }, [customer.id, currentEnvironment?.id])

  useEffect(() => {
    loadInvoices()
  }, [loadInvoices])

  const getStatusVariant = (status: InvoiceStatus) => {
    switch (status) {
      case INVOICE_STATUS.approved:
      case INVOICE_STATUS.sent:
        return 'secondary'
      case INVOICE_STATUS.draft:
      case INVOICE_STATUS.pending_approval:
        return 'outline'
      case INVOICE_STATUS.void:
      case INVOICE_STATUS.uncollectible:
      case INVOICE_STATUS.disputed:
        return 'destructive'
      case INVOICE_STATUS.viewed:
        return 'default'
      default:
        return 'outline'
    }
  }

  const getStatusIcon = (status: InvoiceStatus) => {
    switch (status) {
      case INVOICE_STATUS.approved:
      case INVOICE_STATUS.sent:
      case INVOICE_STATUS.viewed:
        return <CheckCircle className='w-4 h-4' />
      case INVOICE_STATUS.pending_approval:
        return <Clock className='w-4 h-4' />
      case INVOICE_STATUS.draft:
        return <FileText className='w-4 h-4' />
      case INVOICE_STATUS.void:
      case INVOICE_STATUS.uncollectible:
      case INVOICE_STATUS.disputed:
        return <XCircle className='w-4 h-4' />
      default:
        return <AlertCircle className='w-4 h-4' />
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  // Navigation function
  const handleViewInvoiceDetails = (invoice: Invoice) => {
    navigate({
      to: '/invoice-detail/$invoiceId',
      params: { invoiceId: invoice.id },
      search: { from: 'customer' },
    })
  }

  // Dialog handlers
  const handleRecordPayment = (invoice: Invoice, event?: React.MouseEvent) => {
    event?.stopPropagation()
    setInvoiceForAction(invoice)
    setShowRecordPaymentDialog(true)
  }

  const handleUpdateStatus = (invoice: Invoice, event?: React.MouseEvent) => {
    event?.stopPropagation()
    setInvoiceForAction(invoice)
    setShowUpdateStatusDialog(true)
  }

  const handleSendEmail = (invoice: Invoice, event?: React.MouseEvent) => {
    event?.stopPropagation()
    setInvoiceForAction(invoice)
    setShowEmailDialog(true)
  }

  // Action handlers
  const handleFinalize = async (invoice: Invoice, event?: React.MouseEvent) => {
    event?.stopPropagation()
    if (
      !confirm(
        `Finalize invoice ${invoice.invoice_number}? This cannot be undone.`
      )
    ) {
      return
    }
    try {
      await invoicesService.finalizeInvoice(invoice.id)
      toast.success('Invoice finalized successfully')
      loadInvoices()
    } catch {
      toast.error('Failed to finalize invoice')
    }
  }

  const handleVoid = async (invoice: Invoice, event?: React.MouseEvent) => {
    event?.stopPropagation()
    const reason = prompt('Reason for voiding this invoice:')
    if (!reason) {
      return
    }
    try {
      await invoicesService.voidInvoice(invoice.id, { reason })
      toast.success('Invoice voided successfully')
      loadInvoices()
    } catch {
      toast.error('Failed to void invoice')
    }
  }

  const handleMarkAsPaid = async (
    invoice: Invoice,
    event?: React.MouseEvent
  ) => {
    event?.stopPropagation()
    if (!confirm(`Mark invoice ${invoice.invoice_number} as fully paid?`)) {
      return
    }
    try {
      await paymentsService.recordPayment({
        invoice_id: invoice.id,
        amount: invoice.amount_due_cents / 100,
        currency: invoice.currency,
        payment_method: PAYMENT_METHOD.other,
        notes: 'Marked as paid',
      })
      toast.success('Invoice marked as paid')
      loadInvoices()
    } catch {
      toast.error('Failed to mark invoice as paid')
    }
  }

  const handleEmailSubmit = (_data: {
    recipient: string
    subject: string
    message: string
  }) => {
    // TODO: Send email via API
    setInvoiceForAction(null)
  }

  // Define table columns
  const invoiceColumns: ColumnDef<Invoice>[] = [
    {
      accessorKey: 'invoice_number',
      header: 'Invoice ID',
      size: 250,
      cell: ({ row }) => (
        <div>
          <div className='font-medium'>{row.original.invoice_number}</div>
          <div className='text-sm text-muted-foreground'>{row.original.id}</div>
        </div>
      ),
    },
    {
      accessorKey: 'total_cents',
      header: 'Amount',
      size: 250,
      cell: ({ row }) => (
        <div>
          <div className='font-medium'>
            {formatCentsAsCurrency(
              row.original.total_cents,
              row.original.currency
            )}
          </div>
          {row.original.tax_cents > 0 && (
            <div className='text-sm text-muted-foreground'>
              +
              {formatCentsAsCurrency(
                row.original.tax_cents,
                row.original.currency
              )}{' '}
              tax
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 200,
      cell: ({ row }) => {
        const isOverdue =
          row.original.payment_status === 'overdue' ||
          (row.original.status === INVOICE_STATUS.sent &&
            row.original.due_date &&
            new Date(row.original.due_date) < new Date())
        return (
          <div className='flex flex-col gap-1'>
            <Badge
              variant={getStatusVariant(row.original.status)}
              className='gap-1 w-fit'
            >
              {getStatusIcon(row.original.status)}
              {row.original.status.charAt(0).toUpperCase() +
                row.original.status.slice(1).replace('_', ' ')}
            </Badge>
            {isOverdue && (
              <Badge variant='destructive' className='text-xs w-fit'>
                Overdue
              </Badge>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'payment_status',
      header: 'Payment Status',
      size: 200,
      cell: ({ row }) => {
        const paymentStatus = row.original.payment_status
        if (paymentStatus === 'paid') {
          return (
            <div className='flex items-center gap-1 text-green-600'>
              <CheckCircle className='w-4 h-4' />
              <span className='text-sm'>Paid</span>
            </div>
          )
        }
        if (paymentStatus === 'partially_paid') {
          return (
            <div className='flex items-center gap-1 text-blue-600'>
              <Clock className='w-4 h-4' />
              <span className='text-sm'>Partial</span>
            </div>
          )
        }
        if (paymentStatus === 'overdue') {
          return (
            <div className='flex items-center gap-1 text-red-600'>
              <AlertCircle className='w-4 h-4' />
              <span className='text-sm'>Overdue</span>
            </div>
          )
        }
        if (paymentStatus === 'refunded') {
          return (
            <div className='flex items-center gap-1 text-purple-600'>
              <RefreshCw className='w-4 h-4' />
              <span className='text-sm'>Refunded</span>
            </div>
          )
        }
        return (
          <div className='flex items-center gap-1 text-muted-foreground'>
            <FileText className='w-4 h-4' />
            <span className='text-sm'>Unpaid</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'issue_date',
      header: 'Issued',
      size: 130,
      cell: ({ row }) => (
        <div className='flex items-center gap-1'>
          <Calendar className='w-4 h-4 text-muted-foreground' />
          <span className='text-sm'>{formatDate(row.original.issue_date)}</span>
        </div>
      ),
    },
    {
      accessorKey: 'due_date',
      header: 'Due Date',
      size: 130,
      cell: ({ row }) => {
        if (!row.original.due_date) {
          return <span className='text-sm text-muted-foreground'>-</span>
        }
        const isOverdue =
          row.original.payment_status !== 'paid' &&
          new Date(row.original.due_date) < new Date()
        return (
          <div
            className={`flex items-center gap-1 ${isOverdue ? 'text-red-600' : ''}`}
          >
            <Calendar className='w-4 h-4 text-muted-foreground' />
            <span className='text-sm'>{formatDate(row.original.due_date)}</span>
          </div>
        )
      },
    },
    {
      id: 'actions',
      header: '',
      size: 80,
      cell: ({ row }) => {
        const inv = row.original
        const isDraft = inv.status === INVOICE_STATUS.draft
        const isVoid = inv.status === INVOICE_STATUS.void
        const isPaid = inv.payment_status === 'paid'

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' size='sm' className='h-8 w-8 p-0'>
                <MoreHorizontal className='w-4 h-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='w-48'>
              <DropdownMenuItem onClick={() => handleViewInvoiceDetails(inv)}>
                <Eye className='w-4 h-4 mr-2' />
                View Details
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              {isDraft && (
                <DropdownMenuItem onClick={e => handleFinalize(inv, e)}>
                  <FileCheck className='w-4 h-4 mr-2' />
                  Finalize
                </DropdownMenuItem>
              )}

              {!isPaid && !isVoid && (
                <>
                  <DropdownMenuItem onClick={e => handleRecordPayment(inv, e)}>
                    <CreditCard className='w-4 h-4 mr-2' />
                    Record Payment
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={e => handleMarkAsPaid(inv, e)}>
                    <CheckCircle className='w-4 h-4 mr-2' />
                    Mark as Paid
                  </DropdownMenuItem>
                </>
              )}

              <DropdownMenuItem onClick={e => handleUpdateStatus(inv, e)}>
                <Edit className='w-4 h-4 mr-2' />
                Update Status
              </DropdownMenuItem>

              {!isVoid && (
                <DropdownMenuItem
                  onClick={e => handleVoid(inv, e)}
                  className='text-destructive'
                >
                  <XCircle className='w-4 h-4 mr-2' />
                  Void
                </DropdownMenuItem>
              )}

              <DropdownMenuSeparator />

              <DropdownMenuItem onClick={e => handleSendEmail(inv, e)}>
                <Mail className='w-4 h-4 mr-2' />
                Send Email
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation()
                  toast.info('Coming soon')
                }}
              >
                <Send className='w-4 h-4 mr-2' />
                Resend Invoice
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation()
                  toast.info('Coming soon')
                }}
              >
                <Receipt className='w-4 h-4 mr-2' />
                Issue Credit Note
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]

  if (loading) {
    return (
      <div className='space-y-6'>
        {/* Header Skeleton */}
        <div className='flex justify-between items-center'>
          <div className='animate-pulse'>
            <div className='h-8 bg-slate-300 dark:bg-slate-600 rounded w-48 mb-2' />
            <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded w-80' />
          </div>
          <div className='animate-pulse'>
            <div className='h-10 bg-slate-300 dark:bg-slate-600 rounded w-32' />
          </div>
        </div>

        {/* Cards Skeleton */}
        <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
          {Array.from({ length: 4 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
            <Card key={i} className='bg-transparent border'>
              <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                <div className='animate-pulse'>
                  <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded w-20' />
                </div>
                <div className='animate-pulse'>
                  <div className='h-4 w-4 bg-slate-300 dark:bg-slate-600 rounded' />
                </div>
              </CardHeader>
              <CardContent>
                <div className='animate-pulse'>
                  <div className='h-8 bg-slate-300 dark:bg-slate-600 rounded w-24 mb-1' />
                  <div className='h-3 bg-slate-300 dark:bg-slate-600 rounded w-16' />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Table Skeleton */}
        <Card className='bg-transparent border'>
          <CardHeader>
            <div className='animate-pulse'>
              <div className='h-6 bg-slate-300 dark:bg-slate-600 rounded w-32 mb-2' />
              <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded w-48' />
            </div>
          </CardHeader>
          <CardContent>
            <div className='animate-pulse space-y-4'>
              <div className='grid grid-cols-7 gap-4 pb-4 border-b'>
                {Array.from({ length: 7 }).map((_, i) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                    key={i}
                    className='h-4 bg-slate-300 dark:bg-slate-600 rounded'
                  />
                ))}
              </div>
              {Array.from({ length: 5 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                <div key={i} className='grid grid-cols-7 gap-4 py-3'>
                  {Array.from({ length: 7 }).map((_, j) => (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                      key={j}
                      className='h-4 bg-slate-300 dark:bg-slate-600 rounded'
                    />
                  ))}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex justify-between items-center'>
        <div>
          <h2 className='text-2xl font-bold'>Invoices</h2>
        </div>
        <CreateInvoiceDialog
          preselectedCustomerId={customer.id}
          onInvoiceCreated={loadInvoices}
        />
      </div>

      {/* Summary Cards */}
      <div className='grid grid-cols-1 md:grid-cols-4 gap-3'>
        <Card className='bg-transparent border h-24 flex items-center'>
          <CardContent className='px-3 py-0 w-full'>
            <div className='flex items-center justify-between'>
              <span className='text-md font-medium'>Total Revenue</span>
              <span className='text-md font-bold'>
                {formatCentsAsCurrency(
                  summary?.total_revenue_cents ?? 0,
                  invoices[0]?.currency || 'USD'
                )}
              </span>
            </div>
            <p className='text-xs text-muted-foreground mt-1'>
              From paid invoices
            </p>
          </CardContent>
        </Card>

        <Card className='bg-transparent border h-24 flex items-center'>
          <CardContent className='px-3 py-0 w-full'>
            <div className='flex items-center justify-between'>
              <span className='text-md font-medium'>Total Invoices</span>
              <span className='text-md font-bold'>
                {summary?.total_count ?? invoices.length}
              </span>
            </div>
            <p className='text-xs text-muted-foreground mt-1'>All time</p>
          </CardContent>
        </Card>

        <Card className='bg-transparent border h-24 flex items-center'>
          <CardContent className='px-3 py-0 w-full'>
            <div className='flex items-center justify-between'>
              <span className='text-md font-medium'>Outstanding</span>
              <span className='text-md font-bold'>
                {formatCentsAsCurrency(
                  summary?.outstanding_cents ?? 0,
                  invoices[0]?.currency || 'USD'
                )}
              </span>
            </div>
            <p className='text-xs text-muted-foreground mt-1'>
              Pending payment
            </p>
          </CardContent>
        </Card>

        <Card className='bg-transparent border h-24 flex items-center'>
          <CardContent className='px-3 py-0 w-full'>
            <div className='flex items-center justify-between'>
              <span className='text-md font-medium'>Overdue</span>
              <span className='text-md font-bold text-red-600'>
                {summary?.overdue_count ?? 0}
              </span>
            </div>
            <p className='text-xs text-muted-foreground mt-1'>Past due date</p>
          </CardContent>
        </Card>
      </div>

      {/* Invoices Table */}
      <div className='bg-transparent border'>
        {invoices.length > 0 ? (
          <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50 [&_tbody_tr]:cursor-pointer'>
            <EnhancedTable
              data={invoices}
              columns={invoiceColumns.map(col => ({
                ...col,
                enableSorting: false,
              }))}
              height='500px'
              className='bg-transparent'
              onRowClick={row => handleViewInvoiceDetails(row)}
            />
          </div>
        ) : (
          <Card>
            <CardContent>
              <div className='text-center py-12 text-muted-foreground'>
                <FileText className='w-12 h-12 mx-auto mb-4 opacity-50' />
                <h3 className='text-lg font-medium mb-2'>No invoices found</h3>
                <p className='text-sm max-w-sm mx-auto mb-4'>
                  This customer doesn't have any invoices yet. Invoices will be
                  generated automatically based on usage and contracts.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Shared Invoice Action Dialogs */}
      <RecordPaymentDialog
        invoice={invoiceForAction}
        open={showRecordPaymentDialog}
        onOpenChange={setShowRecordPaymentDialog}
        onSuccess={loadInvoices}
      />

      <UpdateInvoiceStatusDialog
        invoice={invoiceForAction}
        open={showUpdateStatusDialog}
        onOpenChange={setShowUpdateStatusDialog}
        onSuccess={loadInvoices}
      />

      <SendInvoiceEmailDialog
        invoice={
          invoiceForAction
            ? {
                id: invoiceForAction.id,
                number: invoiceForAction.invoice_number,
                customer_name: customer.name,
                customer_email: customer.email || undefined,
              }
            : null
        }
        open={showEmailDialog}
        onOpenChange={setShowEmailDialog}
        onSubmit={handleEmailSubmit}
      />
    </div>
  )
}
